import { useEffect, useMemo, useRef, useState } from "react";
import { Activity, TrendingUp, Building2, Target, Zap, Eye } from "lucide-react";
import { api } from "@/lib/api";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${Number(n).toFixed(0)}`;
};

// Bloomberg-style ticker bar. Pulls the latest firms, synergies, and pipeline value, then renders
// a horizontally-scrolling marquee. Refreshes every 60s. Animations are CSS-only (no JS frame work).
export default function TickerBar() {
    const [items, setItems] = useState([]);
    const [pulse, setPulse] = useState(0); // forces a re-paint of the LIVE dot on refresh
    const refreshTimer = useRef(null);

    const load = async () => {
        try {
            const [firmsR, synR, pipR] = await Promise.allSettled([
                api.get("/firms"),
                api.get("/synergies"),
                api.get("/requests/pipeline-value").catch(() => ({ data: null })),
            ]);
            const firms = firmsR.status === "fulfilled" ? (firmsR.value.data || []) : [];
            const syns = synR.status === "fulfilled" ? (synR.value.data || []) : [];
            const pip = pipR.status === "fulfilled" ? pipR.value.data : null;

            const tickerItems = [];

            // Pipeline expected value — flagship live KPI
            if (pip && (pip.total_expected || pip.total_gross)) {
                tickerItems.push({
                    icon: TrendingUp, tone: "emerald",
                    label: "PIPELINE",
                    value: fmtMoney(pip.total_expected || 0),
                    detail: `expected · ${fmtMoney(pip.total_gross || 0)} gross`,
                });
            }

            // 3 newest firms (sorted by updated_at desc, fall back to created_at)
            const sortedFirms = [...firms].sort((a, b) => {
                const ax = a.updated_at || a.created_at || "";
                const bx = b.updated_at || b.created_at || "";
                return bx.localeCompare(ax);
            }).slice(0, 4);
            sortedFirms.forEach((f) => {
                const rev = (f.products || []).reduce((s, p) => s + (Number(p.annual_revenue_usd) || 0), 0);
                tickerItems.push({
                    icon: Building2, tone: "amber",
                    label: f.name.toUpperCase().slice(0, 18),
                    value: fmtMoney(rev),
                    detail: f.industry || "—",
                });
            });

            // 3 highest-fit synergies
            const topSyns = [...syns].sort((a, b) => (b.fit_score || 0) - (a.fit_score || 0)).slice(0, 4);
            topSyns.forEach((s) => {
                tickerItems.push({
                    icon: Target, tone: s.fit_score >= 85 ? "emerald" : s.fit_score >= 75 ? "amber" : "zinc",
                    label: `${(s.firm_a_name || "").slice(0, 8)}×${(s.firm_b_name || "").slice(0, 8)}`.toUpperCase(),
                    value: `${s.fit_score || 0} FIT`,
                    detail: fmtMoney(s.projected_revenue || 0),
                });
            });

            // Heartbeat
            tickerItems.push({
                icon: Zap, tone: "zinc",
                label: "AFFYNIX",
                value: `${firms.length} FIRMS`,
                detail: `${syns.length} synergies live`,
            });

            setItems(tickerItems);
            setPulse((n) => n + 1);
        } catch {
            // silent — ticker is non-critical
        }
    };

    useEffect(() => {
        load();
        refreshTimer.current = setInterval(load, 60_000);
        return () => clearInterval(refreshTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Duplicate items for seamless marquee loop
    const loopItems = useMemo(() => [...items, ...items], [items]);
    const time = new Date().toLocaleTimeString("en-US", { hour12: false });

    if (items.length === 0) return null;

    return (
        <div className="ticker-bar border border-amber-500/20 bg-black mb-6 overflow-hidden relative" data-testid="dashboard-ticker">
            <div className="flex items-center gap-3 border-b border-amber-500/10 px-3 py-1.5 bg-zinc-950/80">
                <span className="flex items-center gap-1.5">
                    <span key={pulse} className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping-once" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-emerald-400">LIVE</span>
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-600">AFFYNIX · WORKSPACE TICKER</span>
                <span className="ml-auto font-mono text-[9px] tracking-wider text-zinc-600 flex items-center gap-1.5">
                    <Activity className="w-3 h-3" /> {time}
                </span>
            </div>
            <div className="ticker-track flex items-center gap-8 py-2.5 whitespace-nowrap">
                {loopItems.map((it, i) => {
                    const Icon = it.icon;
                    const tone = it.tone === "emerald" ? "text-emerald-400" : it.tone === "amber" ? "text-amber-400" : "text-zinc-400";
                    return (
                        <div key={i} className="flex items-center gap-2 font-mono text-xs">
                            <Icon className={`w-3 h-3 ${tone}`} />
                            <span className="uppercase tracking-wider text-zinc-500">{it.label}</span>
                            <span className={`font-bold ${tone}`}>{it.value}</span>
                            <span className="text-zinc-600 lowercase">· {it.detail}</span>
                            <span className="text-zinc-800 mx-2">|</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Counts up a number from 0 to `value` over ~900ms. Used to make stat cards feel alive.
export function AnimatedNumber({ value, formatter, className = "", testId }) {
    const [display, setDisplay] = useState(0);
    const startRef = useRef(0);
    const fromRef = useRef(0);
    const toRef = useRef(value || 0);

    useEffect(() => {
        const from = display;
        const to = Number(value) || 0;
        if (from === to) return;
        fromRef.current = from;
        toRef.current = to;
        startRef.current = performance.now();
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - startRef.current) / 900);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - t, 3);
            const v = fromRef.current + (toRef.current - fromRef.current) * eased;
            setDisplay(v);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const text = formatter ? formatter(display) : Math.round(display).toLocaleString();
    return <span className={className} data-testid={testId}>{text}</span>;
}

export { Eye };
